import React from "react";
import { Box, alpha } from "@mui/material";
import { COLORS } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";

const SIZES = {
  small: { px: 1.5, py: 0.5, fontSize: "0.75rem", minHeight: 28, iconSize: 14 },
  medium: { px: 2, py: 0.875, fontSize: "0.8125rem", minHeight: 34, iconSize: 16 },
  // Form satırında küçük (small) outlined input / tarih kutusuyla aynı yükseklik (40px)
  field: { px: 2.25, py: 1, fontSize: "0.8125rem", minHeight: 40, iconSize: 16 },
};

/**
 * Seçilebilir chip grubu (tekli veya çoklu seçim) — çalışma tipi, gün,
 * kategori gibi sayfalarda elle yazılan
 * `selected ? alpha(primary, .1) : slate50` + border kopyasının tek karşılığı.
 * Seçili chip: tema rengi açık zemin + tema rengi kenarlık + tema rengi metin.
 *
 * Erişilebilirlik: tekli seçimde `radiogroup` / `radio` (aria-checked),
 * çoklu seçimde `group` + `aria-pressed` butonlar. Klavye odağı görünür.
 *
 * @param {Array<{value: string, label: React.ReactNode, icon?: React.ReactNode, disabled?: boolean}>} options
 * @param {string|string[]} value - tekli: string, çoklu: string[]
 * @param {(next: string|string[]) => void} onChange - tekli: seçilen değer, çoklu: yeni dizi
 * @param {boolean} [multiple=false]
 * @param {"small"|"medium"|"field"} [size="small"] - "field": yanındaki form kutularıyla aynı yükseklik
 * @param {boolean} [disabled=false] - tüm grup
 * @param {boolean} [required=false] - çoklu seçimde son seçili öğe kaldırılamaz
 * @param {object} [palette] - verilmezse klinik teması kullanılır
 * @param {string} [ariaLabel] - grup için erişilebilir ad
 * @param {object} [sx] - sarmalayıcı sx
 */
export default function ChoiceChipGroup({
  options = [],
  value,
  onChange,
  multiple = false,
  size = "small",
  disabled = false,
  required = false,
  palette: paletteProp,
  ariaLabel,
  wrapLabels = false,
  sx = {},
}) {
  // wrapLabels (opt-in): uzun etiketler satır kırsın (dar kartlarda taşmasın)
  const { palette: themePalette } = useClinicTheme();
  const palette = paletteProp || themePalette;
  const tone = palette?.primary || COLORS.slate700;
  const s = SIZES[size] || SIZES.small;

  const selectedList = multiple ? (Array.isArray(value) ? value : []) : [];
  const isSelected = (v) => (multiple ? selectedList.includes(v) : value === v);

  const handleClick = (opt) => {
    if (disabled || opt.disabled) return;
    if (!multiple) {
      if (value !== opt.value) onChange?.(opt.value);
      return;
    }
    const selected = selectedList.includes(opt.value);
    if (selected && required && selectedList.length <= 1) return;
    onChange?.(
      selected ? selectedList.filter((v) => v !== opt.value) : [...selectedList, opt.value]
    );
  };

  return (
    <Box
      role={multiple ? "group" : "radiogroup"}
      aria-label={ariaLabel}
      aria-disabled={disabled || undefined}
      sx={{ display: "flex", flexWrap: "wrap", gap: 1, ...sx }}
    >
      {options.map((opt) => {
        const selected = isSelected(opt.value);
        const optDisabled = disabled || Boolean(opt.disabled);
        const a11y = multiple
          ? { "aria-pressed": selected }
          : { role: "radio", "aria-checked": selected };
        return (
          <Box
            key={opt.value}
            component="button"
            type="button"
            disabled={optDisabled}
            onClick={() => handleClick(opt)}
            {...a11y}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              px: s.px,
              py: s.py,
              minHeight: s.minHeight,
              borderRadius: wrapLabels ? 2 : 999,
              textAlign: "left",
              fontFamily: "inherit",
              fontSize: s.fontSize,
              fontWeight: selected ? 700 : 600,
              lineHeight: 1.2,
              whiteSpace: wrapLabels ? "normal" : "nowrap",
              cursor: optDisabled ? "default" : "pointer",
              bgcolor: selected ? alpha(tone, 0.1) : COLORS.slate50,
              color: selected ? tone : COLORS.slate500,
              border: `1px solid ${selected ? tone : COLORS.slate200}`,
              opacity: optDisabled ? 0.55 : 1,
              transition: "background-color 150ms ease, border-color 150ms ease, color 150ms ease",
              "& svg": { fontSize: s.iconSize, width: s.iconSize, height: s.iconSize },
              "&:hover": optDisabled
                ? {}
                : { bgcolor: selected ? alpha(tone, 0.16) : COLORS.slate100 },
              "&:focus-visible": { outline: `2px solid ${tone}`, outlineOffset: 2 },
            }}
          >
            {opt.icon ? (
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                {opt.icon}
              </Box>
            ) : null}
            {opt.label}
          </Box>
        );
      })}
    </Box>
  );
}
